import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import SearchModal from './SearchModal';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.projects'), path: '/projects' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' },
    { name: t('nav.faq'), path: '/faq' },
  ];

  // Health Dashboard link - will be at /health after deployment
  const healthDashboardUrl = '/health';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0B1220]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="group">
            <Logo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-base font-medium transition-colors duration-200 relative group ${
                  location.pathname === link.path
                    ? 'text-yamai-cyan-400'
                    : 'text-[#8A90A0] hover:text-white'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-0.5 bg-gradient-to-r from-yamai-cyan-500 to-yamai-purple-600 transition-transform duration-300 origin-left ${
                    location.pathname === link.path ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
            
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-yamai-cyan-400"
              aria-label="Search"
            >
              <Search size={20} />
            </button>
            
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Health Dashboard Icon with Tooltip */}
            <div className="relative group ml-4">
              <a
                href={healthDashboardUrl}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-yamai-cyan-500 to-yamai-purple-600 hover:shadow-lg hover:shadow-yamai-cyan-500/50 transition-all duration-300 hover:scale-110"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/>
                </svg>
              </a>
              
              {/* Tooltip Card */}
              <div className="absolute right-0 top-full mt-3 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-yamai-cyan-500/20 p-4 transform translate-y-2 group-hover:translate-y-0">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yamai-cyan-500 to-yamai-purple-600 flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                      </svg>
                    </div>
                    <h3 className="font-bold text-gray-900 text-sm">Health Dashboard</h3>
                  </div>
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">
                    Track your health metrics, connect Fitbit devices, and monitor your wellness journey in real-time.
                  </p>
                  <div className="flex items-center gap-2 text-xs">
                    <a
                      href={`${healthDashboardUrl}/login`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 bg-yamai-cyan-100 text-yamai-cyan-700 rounded-md font-medium hover:bg-yamai-cyan-200 transition-colors cursor-pointer"
                    >
                      Sign In
                    </a>
                    <span className="text-gray-400">or</span>
                    <a
                      href={`${healthDashboardUrl}/register`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-2 py-1 bg-yamai-purple-100 text-yamai-purple-700 rounded-md font-medium hover:bg-yamai-purple-200 transition-colors cursor-pointer"
                    >
                      Create Account
                    </a>
                  </div>
                </div>
                {/* Arrow */}
                <div className="absolute -top-2 right-4 w-4 h-4 bg-white/95 border-l border-t border-yamai-cyan-500/20 transform rotate-45"></div>
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#0B1220]/98 backdrop-blur-md border-t border-[#1F2A3A]">
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-200 ${
                  location.pathname === link.path
                    ? 'bg-gradient-to-r from-yamai-cyan-500/20 to-yamai-purple-500/20 text-yamai-cyan-400 border border-yamai-cyan-400/30'
                    : 'text-[#8A90A0] hover:bg-white/5 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Mobile Search & Language */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <button
                onClick={() => {
                  setIsSearchOpen(true);
                  setIsMobileMenuOpen(false);
                }}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-white font-medium flex items-center justify-center gap-2"
              >
                <Search size={18} />
                Search
              </button>
              <div className="flex-shrink-0">
                <LanguageSwitcher />
              </div>
            </div>
            
            {/* Mobile Health Dashboard Card */}
            <div className="pt-4 border-t border-white/10">
              <div className="bg-gradient-to-br from-yamai-cyan-500/10 to-yamai-purple-600/10 backdrop-blur-sm rounded-2xl p-4 border border-yamai-cyan-500/20">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yamai-cyan-500 to-yamai-purple-600 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                    </svg>
                  </div>
                  <h3 className="font-bold text-white text-sm">Health Dashboard</h3>
                </div>
                <p className="text-xs text-white/70 mb-3 leading-relaxed">
                  Track health metrics, connect Fitbit, and monitor your wellness journey
                </p>
                <div className="flex gap-2">
                  <a
                    href={`${healthDashboardUrl}/login`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-yamai-cyan-500 to-yamai-cyan-600 text-white font-semibold text-center text-sm hover:shadow-lg transition-all duration-300"
                  >
                    Sign In
                  </a>
                  <a
                    href={`${healthDashboardUrl}/register`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-yamai-purple-500 to-yamai-purple-600 text-white font-semibold text-center text-sm hover:shadow-lg transition-all duration-300"
                  >
                    Sign Up
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </nav>
  );
};

export default Navbar;
